// Global Instructions Rule Applied!

import React, { useState, useEffect, useCallback, useMemo, forwardRef } from 'react'
import { debounce } from 'lodash'
import Select from '../../Select.jsx'
import { useCompanySearch } from '../../../hooks_deprecated/useCompanySearch.js'
import { InputSize, FieldValue } from '../types.ts'
import { SelectListEntry } from '../../../../state_manager/data_models/select_list_entry.ts'

export interface CompanySelectProps {
  value: FieldValue
  onChange?: (value: string | string[] | FieldValue, option?: SelectListEntry | SelectListEntry[] | null) => void
  onSelect?: (value: string, option?: SelectListEntry) => void
  onClear?: () => void
  mode?: 'form' | 'inline' | 'cell' | 'single' | 'multiple'
  selectMode?: 'single' | 'multiple'
  contextKey?: string
  getCompanyDetailsById?: (id: string) => Record<string, any> | undefined
  onCompanyDetailsUpdate?: (companyDetails: Record<string, any>) => void
  required?: boolean
  placeholder?: string
  size?: InputSize
  error?: boolean
  style?: React.CSSProperties
  className?: string
  autoFocus?: boolean
  onKeyDown?: (e: React.KeyboardEvent) => void
  disabled?: boolean
}

export const CompanySelect = forwardRef<any, CompanySelectProps>(
  (
    {
      value,
      onChange,
      onSelect,
      onClear,
      mode = 'form',
      selectMode = 'single',
      contextKey,
      getCompanyDetailsById,
      onCompanyDetailsUpdate,
      required = false,
      placeholder = 'Search companies...',
      size = 'small',
      error = false,
      style = {},
      className = '',
      autoFocus,
      onKeyDown,
      disabled,
      ...selectProps
    },
    ref
  ) => {
    const {
      searchCompanies,
      loading: companySearchLoading,
      results: companySearchResults,
      clearResults: clearCompanyResults,
      hasSearched: companyHasSearched
    } = useCompanySearch()

    const isMultiple = selectMode === 'multiple'
    const [selectedCompany, setSelectedCompany] = useState<SelectListEntry | null>(null)
    const [selectedCompanies, setSelectedCompanies] = useState<SelectListEntry[]>([])

    // Extract initial value options from value prop for multiple mode
    const initialValueOptions = useMemo<SelectListEntry[]>(() => {
      if (!value) return []

      if (isMultiple) {
        if (Array.isArray(value)) {
          return value.map((v: any) => {
            if (typeof v === 'object' && v.value !== undefined && v.label) {
              return {
                label: v.label,
                value: v.value,
                name: v.name || v.label
              }
            }
            // Fallback for string values
            return {
              label: v,
              value: v,
              name: v
            }
          })
        }
        return []
      } else {
        if (typeof value === 'object' && value && (value as any).value !== undefined && (value as any).label) {
          const v = value as any
          return [
            {
              label: v.label,
              value: v.value,
              name: v.name || v.label
            }
          ]
        }
        return []
      }
    }, [value, isMultiple])

    const initializeCompanySelection = useCallback(
      (companyVal: any) => {
        if (isMultiple) {
          if (Array.isArray(companyVal) && companyVal.length > 0) {
            const companies = companyVal.map((v: any) => {
              if (typeof v === 'object' && v.value !== undefined) {
                return {
                  label: v.label || v.name || v.value,
                  value: v.value,
                  name: v.name || v.label || v.value
                }
              }
              return {
                label: v,
                value: v,
                name: v
              }
            })
            setSelectedCompanies(companies)
          } else {
            setSelectedCompanies([])
          }
        } else {
          // Handle object values (e.g., { value: id, label: name })
          let companyId: string = ''
          let companyLabel: string = ''

          if (typeof companyVal === 'object' && companyVal !== null) {
            companyId = (companyVal as any).value || (companyVal as any).id || ''
            companyLabel = (companyVal as any).label || (companyVal as any).name || ''
          } else if (companyVal) {
            companyId = String(companyVal)
          }

          // Check if value has changed
          const currentValue = selectedCompany?.value || ''
          const valueChanged = companyId !== currentValue

          if (companyVal && (!selectedCompany || valueChanged)) {
            // If we already have a label from the object, use it directly
            if (companyLabel && companyId) {
              const selectListEntry: SelectListEntry = {
                label: companyLabel,
                value: companyId,
                name: companyLabel
              }
              setSelectedCompany(selectListEntry)
            } else if (companyId) {
              // Try to find in search results
              const existingCompany = companySearchResults.find(
                (company: any) => company.value === companyId || company.label === companyId
              )

              if (existingCompany) {
                const selectListEntry: SelectListEntry = {
                  label: existingCompany.label,
                  value: existingCompany.value,
                  name: existingCompany.label
                }
                setSelectedCompany(selectListEntry)
              } else if (getCompanyDetailsById) {
                // Try to look up by ID
                const companyDetails = getCompanyDetailsById(companyId)
                if (companyDetails) {
                  const companyOption: SelectListEntry = {
                    label: companyDetails.name || companyDetails.legal_name || '',
                    value: companyDetails.id || companyId,
                    name: companyDetails.name || companyDetails.legal_name || ''
                  }
                  setSelectedCompany(companyOption)
                } else {
                  // Fallback: use the ID as label if no lookup available
                  const tempCompany: SelectListEntry = {
                    label: companyLabel || companyId,
                    value: companyId,
                    name: companyLabel || companyId
                  }
                  setSelectedCompany(tempCompany)
                }
              } else {
                // No lookup function available, use what we have
                const tempCompany: SelectListEntry = {
                  label: companyLabel || companyId,
                  value: companyId,
                  name: companyLabel || companyId
                }
                setSelectedCompany(tempCompany)
              }
            }
          } else if (!companyVal) {
            setSelectedCompany(null)
          }
        }
      },
      [companySearchResults, getCompanyDetailsById, selectedCompany, isMultiple]
    )

    useEffect(() => {
      initializeCompanySelection(value)
    }, [value, initializeCompanySelection])

    // Sync selectedCompanies with initialValueOptions for multiple mode
    useEffect(() => {
      if (isMultiple) {
        setSelectedCompanies(initialValueOptions)
      }
    }, [initialValueOptions, isMultiple])

    const handleCompanySelect = useCallback(
      (selectedValue: any, option: any) => {
        if (isMultiple) {
          if (Array.isArray(selectedValue)) {
            const transformedValues = selectedValue.map((val: any) => {
              // Find existing company data from current value prop
              const existingCompany = Array.isArray(value)
                ? value.find((v: any) => {
                    const vValue = typeof v === 'object' && v.value !== undefined ? v.value : v
                    return vValue === val
                  })
                : null

              // Try to find option from current options list
              const existingOption =
                companySearchResults.find((opt: any) => opt.value === val) ||
                initialValueOptions.find((opt) => opt.value === val)

              // Preserve existing company data, fallback to option, then use val
              return {
                label: existingCompany?.label || existingOption?.label || existingOption?.name || val,
                value: val,
                name: existingCompany?.name || existingOption?.name || existingOption?.label || val
              }
            })
            setSelectedCompanies(transformedValues)
            onChange?.(transformedValues)
          } else {
            setSelectedCompanies([])
            onChange?.([])
          }
        } else {
          if (option) {
            setSelectedCompany(option)
            onChange?.(selectedValue, option)
            onSelect?.(selectedValue, option)

            if (onCompanyDetailsUpdate && getCompanyDetailsById) {
              const companyDetails = getCompanyDetailsById(option.value)
              if (companyDetails) {
                onCompanyDetailsUpdate(companyDetails)
              }
            }
          }
        }
      },
      [
        onChange,
        onSelect,
        onCompanyDetailsUpdate,
        getCompanyDetailsById,
        isMultiple,
        value,
        companySearchResults,
        initialValueOptions
      ]
    )

    const handleClear = useCallback(() => {
      if (isMultiple) {
        setSelectedCompanies([])
        onChange?.([])
      } else {
        setSelectedCompany(null)
        onChange?.('', null)
      }
      clearCompanyResults()
      onClear?.()
    }, [onChange, onClear, clearCompanyResults, isMultiple])

    // Debounced search function - memoized to prevent recreation on every render
    const debouncedSearch = useMemo(
      () =>
        debounce((searchValue: string) => {
          if (searchValue && searchValue.length >= 2) {
            searchCompanies(searchValue)
          }
        }, 300),
      [searchCompanies]
    )

    const handleSearch = useCallback(
      (searchValue: string) => {
        if (!searchValue) {
          // Clear results immediately when search is empty (no debounce needed)
          clearCompanyResults()
        } else {
          // Use debounced function for search
          debouncedSearch(searchValue)
        }
      },
      [debouncedSearch, clearCompanyResults]
    )

    const transformedResults: SelectListEntry[] = companySearchResults.map((result: any) => ({
      label: result.label,
      value: result.value,
      name: result.label
    }))

    const getDisplayValue = () => {
      if (!value) return undefined

      if (isMultiple) {
        if (Array.isArray(value)) {
          return value.map((v: any) => {
            if (typeof v === 'object' && v.value !== undefined) {
              return v.value
            }
            return typeof v === 'string' ? v : v
          })
        }
        return []
      } else {
        if (typeof value === 'object' && value && (value as any).value !== undefined) {
          return (value as any).value
        }
        return typeof value === 'object' && value ? (value as any).value : value
      }
    }

    // Merge search results with initial value options
    const displayOptions = useMemo(() => {
      if (isMultiple) {
        if (transformedResults.length > 0) {
          const searchValues = new Set(transformedResults.map((opt) => opt.value))
          const mergedInitialOptions = initialValueOptions.filter((opt) => !searchValues.has(opt.value))
          return [...transformedResults, ...mergedInitialOptions]
        }
        return initialValueOptions
      } else {
        if (selectedCompany) {
          return [selectedCompany, ...transformedResults.filter((result) => result.value !== selectedCompany.value)]
        }
        return transformedResults
      }
    }, [transformedResults, selectedCompany, initialValueOptions, isMultiple])

    // For multiple mode, use selectedCompanies for display if available, otherwise use initialValueOptions
    const multipleDisplayValue = useMemo(() => {
      if (isMultiple) {
        if (selectedCompanies.length > 0) {
          return selectedCompanies.map((c) => c.value)
        }
        return initialValueOptions.map((opt) => opt.value)
      }
      return undefined
    }, [isMultiple, selectedCompanies, initialValueOptions])

    const options = displayOptions

    const placeholderText = required ? `${placeholder}` : placeholder

    return (
      <Select
        ref={ref}
        mode={isMultiple ? 'multiple' : undefined}
        value={isMultiple ? multipleDisplayValue : selectedCompany ? selectedCompany.value : undefined}
        onChange={handleCompanySelect}
        onClear={handleClear}
        onSearch={handleSearch}
        loading={companySearchLoading}
        options={options}
        placeholder={placeholderText}
        size={size}
        style={{
          width: '100%',
          ...style,
          borderColor: style.borderColor || (error ? '#ff4d4f' : undefined)
        }}
        className={className}
        status={error ? 'error' : undefined}
        filterOption={false}
        notFoundContent={
          companySearchLoading ? 'Searching...' : companyHasSearched ? 'No companies found' : 'Type to start searching'
        }
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        disabled={disabled}
        allowClear
        {...selectProps}
      />
    )
  }
)

CompanySelect.displayName = 'CompanySelect'
